import { createStore } from 'vuex';
import { clearAlgoliaSearchAttribution, publishAddToCartAttributionToPixel } from '../../search/algolia/attribution';
import cartHelpers from './api';
const { status, fetch, fetchInventory, add, update, change, updateNote, updateDiscount, clear } = cartHelpers;

/** Discount codes currently applied on the cart (code-type only, not automatic). */
export const getAppliedDiscountCodes = (cart = {}) => {
  const codes = [];

  (cart.cart_level_discount_applications || []).forEach((discount) => {
    if (discount.type === 'discount_code' && discount.title) {
      codes.push(discount.title);
    }
  });

  (cart.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      const application = allocation.discount_application;
      if (application?.type === 'discount_code' && application.title) {
        codes.push(application.title);
      }
    });
  });

  return [...new Set(codes)];
};

const getUrlDiscountCode = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return new URLSearchParams(window.location.search).get('discount-code')?.trim() || '';
};

const clearUrlDiscountCodeFromUrl = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  if (!url.searchParams.has('discount-code')) {
    return;
  }

  url.searchParams.delete('discount-code');
  const search = url.searchParams.toString();
  window.history.replaceState({}, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
};

/**
 * Resolves once no other cart operation is in flight, or false if the cart
 * stays busy past the timeout. Discount requests are cheap to defer, and
 * dropping them outright reads to the shopper as a failed code.
 */
const waitForCartReady = (getters, timeout = 8000) => {
  if (getters.isCartReady) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const poll = setInterval(() => {
      if (getters.isCartReady) {
        clearInterval(poll);
        resolve(true);
      } else if (Date.now() - startedAt >= timeout) {
        clearInterval(poll);
        resolve(false);
      }
    }, 50);
  });
};

/** Ensures ?discount-code= is only auto-applied once per page load. */
let urlDiscountApplyAttempted = false;

const req = (api, data) => {
  return new Promise(resolve => {
    api(data).then(data => {
      resolve(data);
    });
  });
};

const afterReq = (data, commit, getters) => {
  commit('setCart', data);

  const _status = getters.cartItems.length ? status.ideal : status.empty;
  commit('setStatus', _status);
};

const pushAlgoliaAddToCartAttribution = (trackingPayload) => {
  if (!trackingPayload || typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'algolia_add_to_cart_attribution',
    algolia: {
      queryID: trackingPayload.queryID,
      // The clicked search record, not the added variant: with distinct:true
      // the added variant may not be in the queryID's results, and position
      // refers to the clicked card.
      objectIDs: [trackingPayload.objectID || trackingPayload.variantId],
      indexName: trackingPayload.index,
      positions: trackingPayload.position != null ? [trackingPayload.position] : [],
    },
  });

  publishAddToCartAttributionToPixel(trackingPayload);
  clearAlgoliaSearchAttribution();
};


export default createStore({
  state: {
    cart: {},
    inventory: [],
    status: status.empty,
    updateIds: [],
    updateLine: 0,
    urlDiscountFeedback: null,
  },

  getters: {
    isCartReady: state => [status.ideal, status.empty].includes(state.status),
    cartItems: state => state.cart.items || [],
    appliedDiscountCodes: state => getAppliedDiscountCodes(state.cart),
    cartLevelDiscounts: state => state.cart.cart_level_discount_applications || [],
  },

  mutations: {
    setCart(state, cart) {
      state.cart = cart;
    },

    addCartItem(state, items) {
      state.cart.items.splice(0, 0, ...items);
    },

    setInventory(state, inventory) {
      state.inventory = inventory
    },

    setUpdateIds(state, ids) {
      state.updateIds = ids;
    },

    setStatus(state, status) {
      state.status = status;
    },
    
    setUpdateLine(state, lines) {
      state.updateLine = lines;
    },

    setUrlDiscountFeedback(state, feedback) {
      state.urlDiscountFeedback = feedback;
    },
  },

  actions: {
    async init({ commit, getters, dispatch }, s1 = status.progress) {
      await commit('setStatus', s1);
      await req(fetchInventory).then(inventory => commit('setInventory', inventory));
      await req(fetch).then(data => afterReq(data, commit, getters));
      await dispatch('applyUrlDiscountIfPresent');
      return true;
    },

    add({ commit, dispatch, getters }, payload) {
      const { item, trackingPayload = null } = payload || {};

      if (getters.isCartReady) {
        commit('setStatus', status.adding);
        return req(add, item).then((response) => {
          pushAlgoliaAddToCartAttribution(trackingPayload);
          return dispatch('init', status.adding).then(() => response);
        });
      }

      return Promise.resolve(null);
    },

    change({ commit, getters }, payload) {
      const { noStateUpdate, cb, trackingPayload = null } = payload;
      const data = { ...payload };
      delete data.cb
      delete data.trackingPayload
      if (getters.isCartReady || noStateUpdate) {
        if(!noStateUpdate) {
          commit('setUpdateLine', data.line);
          commit('setStatus', status.progress);
        }else delete data.noStateUpdate
        
        return req(change, data).then((response) => {
          pushAlgoliaAddToCartAttribution(trackingPayload);
          afterReq(response, commit, getters);
          !noStateUpdate && commit('setUpdateLine', 0);
          cb && cb()
          return response;
        });
      }

      return Promise.resolve(null);
    },

    changeThenAdd({ commit, getters, dispatch }, payload) {
      if (getters.isCartReady) {
        if (payload.line) {
          commit('setUpdateLine', payload.line);
        }
        commit('setStatus', status.progress);

        // Step 1: Process all change operations sequentially
        const processChanges = async () => {
          for (let i = 0; i < payload.change.length; i++) {
            await req(change, payload.change[i]);
          }
        };

        // Step 2: Process add operations after all changes are complete
        return processChanges().then(() => {
          if (payload.add?.length > 0) {
            return req(add, payload.add).then(() => dispatch('init', status.progress));
          }
        }).catch(err => {
          console.error("changeThenAdd failed:", err);
        });
      }
    },

    async update({ commit,dispatch, getters }, data) {
      if (getters.isCartReady) {
        await commit('setUpdateLine', data.line);
        await commit('setStatus', status.progress);
        await req(update, data.data).then(async () => {
          await dispatch('init', status.progess);
        });
        return true;
      }
    },

    updateNote({ commit, getters }, note) {
      if (getters.isCartReady) {
        commit('setStatus', status.progress);
        req(updateNote, note).then(data => afterReq(data, commit, getters));
      }
    },

    /**
     * Set the cart's discount codes (comma-joined). Pass [] to clear.
     * Returns the Ajax cart payload so callers can inspect discount_codes.
     */
    async updateDiscount({ commit, getters }, codes = []) {
      // Wait rather than bail: a quantity change in flight would otherwise make
      // a perfectly valid code report itself as failed.
      if (!(await waitForCartReady(getters))) {
        return null;
      }

      commit('setStatus', status.progress);
      const discount = Array.isArray(codes) ? codes.filter(Boolean).join(',') : String(codes || '');

      return req(updateDiscount, discount).then(async (data) => {
        if (!data || typeof data !== 'object') {
          const fallbackStatus = getters.cartItems.length ? status.ideal : status.empty;
          commit('setStatus', fallbackStatus);
          return null;
        }

        // The update.js response can be a mid-recalculation snapshot: blank
        // discount titles, zero allocated amounts, and a total matching
        // neither (seen live with a 100% code: subtotal $298, discount -$0,
        // total $10). Commit a fresh /cart.js read for display instead, the
        // same way `add` re-inits rather than trusting its POST response.
        // Keep the POST's discount_codes: that echo carries the `applicable`
        // verdict for the code that was just sent.
        const fresh = await req(fetch);
        const display = fresh && typeof fresh === 'object' ? fresh : data;
        afterReq(display, commit, getters);
        return { ...display, discount_codes: data.discount_codes || display.discount_codes };
      });
    },

    /**
     * Apply a single discount code to the cart. Gift cards are not supported
     * here: Shopify only accepts them at checkout.
     * Returns { success, error?, alreadyApplied? }.
     */
    async applyDiscountCode({ dispatch, getters }, code) {
      const trimmedCode = String(code || '').trim();
      if (!trimmedCode) {
        return { success: false, error: null };
      }

      const existing = getters.appliedDiscountCodes;
      if (existing.some((c) => c.toLowerCase() === trimmedCode.toLowerCase())) {
        return { success: true, alreadyApplied: true, error: null };
      }

      const data = await dispatch('updateDiscount', [...existing, trimmedCode]);

      if (!data) {
        return { success: false, error: 'Unable to apply discount. Please try again.' };
      }

      const discountEntry = (data.discount_codes || []).find(
        (entry) => entry.code?.toLowerCase() === trimmedCode.toLowerCase()
      );

      if (discountEntry && discountEntry.applicable === false) {
        return {
          success: false,
          error: `Discount code "${trimmedCode}" is invalid or not applicable.`,
        };
      }

      const codesAfter = getAppliedDiscountCodes(data);
      const stillMissing =
        !codesAfter.some((c) => c.toLowerCase() === trimmedCode.toLowerCase()) &&
        discountEntry?.applicable === true;

      if (stillMissing) {
        return {
          success: false,
          error: 'This discount applies at shipping and will appear at checkout.',
        };
      }

      return { success: true, error: null };
    },

    async applyUrlDiscountIfPresent({ dispatch, commit }) {
      if (urlDiscountApplyAttempted) {
        return;
      }

      const code = getUrlDiscountCode();
      if (!code) {
        return;
      }

      urlDiscountApplyAttempted = true;

      const result = await dispatch('applyDiscountCode', code);
      clearUrlDiscountCodeFromUrl();

      if (result?.error) {
        commit('setUrlDiscountFeedback', { code, message: result.error });
      } else {
        commit('setUrlDiscountFeedback', null);
      }
    },

    clear({ commit, getters }) {
      if (getters.isCartReady) {
        commit('setStatus', status.progress);
        req(clear).then(data => afterReq(data, commit, getters));
      }
    }
  }
});
